import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import api from '../services/api'
import { Plus, Edit, Trash2, Eye, Loader2, Search, Calendar, Landmark, X, Check } from 'lucide-react'
import { useState } from 'react'

const AdminSeminaires = () => {
  const queryClient = useQueryClient()
  const [searchTerm, setSearchTerm] = useState('')
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [currentSeminaire, setCurrentSeminaire] = useState(null)
  const [isUploading, setIsUploading] = useState(false)

  const { data, isLoading } = useQuery({
    queryKey: ['admin-seminaires'],
    queryFn: async () => {
      const res = await api.get('/seminaires/', { params: { limit: 100 } })
      return res.data
    }
  })

  const saveMutation = useMutation({
    mutationFn: (seminaire) => {
      if (seminaire.id) {
        return api.put(`/seminaires/${seminaire.id}`, seminaire)
      } else {
        const slug = seminaire.slug || seminaire.titre.toLowerCase().replace(/ /g, '-').replace(/[^\w-]+/g, '') + '-' + crypto.randomUUID().split('-')[0]
        return api.post('/seminaires/', { ...seminaire, slug })
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['admin-seminaires'])
      setIsModalOpen(false)
      setCurrentSeminaire(null)
    },
    onError: (error) => {
      console.error("Save failed", error)
      alert("Erreur lors de l'enregistrement : " + (error.response?.data?.detail || error.message))
    }
  })

  const deleteMutation = useMutation({
    mutationFn: (id) => api.delete(`/seminaires/${id}`),
    onSuccess: () => queryClient.invalidateQueries(['admin-seminaires'])
  })

  const handleEdit = (seminaire) => {
    setCurrentSeminaire(seminaire)
    setIsModalOpen(true)
  }

  const handleCreate = () => {
    setCurrentSeminaire({
      titre: '',
      entreprise: '',
      date_texte: '',
      financement_fcfa: 0,
      images: []
    })
    setIsModalOpen(true)
  }

  const handleFileChange = async (e) => {
    const files = Array.from(e.target.files)
    if (files.length === 0) return

    setIsUploading(true)
    const uploadedPaths = []

    try {
      for (const file of files) {
        const formData = new FormData()
        formData.append('file', file)
        const res = await api.post('/medias/upload', formData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        })
        uploadedPaths.push(res.data.chemin)
      }
      setCurrentSeminaire({ 
        ...currentSeminaire, 
        images: [...(currentSeminaire.images || []), ...uploadedPaths] 
      })
    } catch (error) {
      console.error("Upload failed", error)
      alert("Échec de l'upload de certaines images")
    } finally {
      setIsUploading(false)
    }
  }

  const removeImage = (index) => {
    const newImages = [...currentSeminaire.images]
    newImages.splice(index, 1)
    setCurrentSeminaire({ ...currentSeminaire, images: newImages })
  }

  const filteredSeminaires = (data?.items || []).filter(seminaire => 
    seminaire.titre.toLowerCase().includes(searchTerm.toLowerCase()) ||
    seminaire.entreprise.toLowerCase().includes(searchTerm.toLowerCase())
  ).sort((a, b) => b.id - a.id)

  if (isLoading) return <div className="flex justify-center py-20"><Loader2 className="animate-spin text-cidh-gold" size={48} /></div>

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h3 className="text-2xl font-bold text-cidh-blue font-montserrat">Gestion des Séminaires</h3>
          <p className="text-gray-500">Gérez les séminaires de formation et renforcement de capacités</p>
        </div>
        <button 
          onClick={handleCreate}
          className="btn-primary flex items-center justify-center gap-2 py-3 px-6 rounded-xl shadow-lg shadow-cidh-gold/20 transition-all hover:scale-105 active:scale-95"
        >
          <Plus size={20} /> Nouveau Séminaire
        </button>
      </div>

      <div className="bg-white p-4 rounded-2xl shadow-sm border border-gray-100 flex flex-col md:flex-row gap-4">
        <div className="relative flex-grow">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
          <input 
            type="text" 
            placeholder="Rechercher par titre ou entreprise..." 
            className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-xl focus:ring-2 focus:ring-cidh-gold/20 focus:border-cidh-gold outline-none transition-all"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
      </div>

      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left">
            <thead className="bg-gray-50 text-gray-500 text-xs uppercase font-bold">
              <tr>
                <th className="px-6 py-4">Séminaire</th>
                <th className="px-6 py-4">Entreprise / Client</th>
                <th className="px-6 py-4">Date</th>
                <th className="px-6 py-4">Financement</th>
                <th className="px-6 py-4 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filteredSeminaires?.map((seminaire) => (
                <tr key={seminaire.id} className="hover:bg-gray-50/50 transition-colors group">
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 rounded-lg bg-cidh-blue/5 flex items-center justify-center text-cidh-blue flex-shrink-0">
                        {seminaire.images && seminaire.images.length > 0 ? (
                           <img 
                           src={`/media/${seminaire.images[0]}`} 
                           className="w-full h-full object-cover rounded-lg"
                           alt={seminaire.titre}
                         />
                        ) : (
                          <Landmark size={24} />
                        )}
                      </div>
                      <div>
                        <div className="font-bold text-cidh-blue text-sm line-clamp-1">{seminaire.titre}</div>
                        <div className="text-xs text-gray-400">Séminaire</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm font-medium text-gray-700">{seminaire.entreprise}</div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-2 text-sm text-gray-500">
                      <Calendar size={14} />
                      {seminaire.date_texte}
                    </div>
                  </td>
                  <td className="px-6 py-4 text-sm font-semibold text-cidh-gold">
                    {seminaire.financement_fcfa ? seminaire.financement_fcfa.toLocaleString() + ' FCFA' : '-'}
                  </td>
                  <td className="px-6 py-4 text-right">
                    <div className="flex justify-end gap-1 opacity-100 md:opacity-0 group-hover:opacity-100 transition-opacity">
                      <a href={`/seminaires/${seminaire.slug}`} target="_blank" rel="noreferrer" className="p-2 text-blue-500 hover:bg-blue-50 rounded-lg transition-colors" title="Voir">
                        <Eye size={18} />
                      </a>
                      <button 
                         onClick={() => handleEdit(seminaire)}
                         className="p-2 text-cidh-gold hover:bg-gold-50 rounded-lg transition-colors" title="Modifier"
                      >
                        <Edit size={18} />
                      </button>
                      <button 
                        onClick={() => window.confirm('Supprimer ce séminaire ?') && deleteMutation.mutate(seminaire.id)}
                        className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                        title="Supprimer"
                      >
                        <Trash2 size={18} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modal Form for Seminaire */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-start justify-center p-4 bg-black/50 backdrop-blur-sm overflow-y-auto pt-10">
          <div className="bg-white rounded-3xl w-full max-w-2xl max-h-[90vh] overflow-y-auto shadow-2xl">
            <div className="p-8 border-b sticky top-0 bg-white z-10 flex justify-between items-center">
              <h4 className="text-xl font-bold text-cidh-blue font-montserrat">
                {currentSeminaire?.id ? 'Modifier le Séminaire' : 'Nouveau Séminaire'}
              </h4>
              <button 
                onClick={() => setIsModalOpen(false)}
                className="p-2 hover:bg-gray-100 rounded-full transition-colors"
              >
                <X size={24} />
              </button>
            </div>
            
            <form className="p-8 space-y-6" onSubmit={(e) => {
              e.preventDefault()
              saveMutation.mutate(currentSeminaire)
            }}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2 md:col-span-2">
                  <label className="text-sm font-bold text-gray-700">Titre du Séminaire *</label>
                  <input 
                    type="text" 
                    required
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-cidh-gold/20 focus:border-cidh-gold outline-none transition-all"
                    value={currentSeminaire?.titre}
                    onChange={(e) => setCurrentSeminaire({...currentSeminaire, titre: e.target.value})}
                  />
                </div>
                
                <div className="space-y-2">
                  <label className="text-sm font-bold text-gray-700">Entreprise / Client *</label>
                  <input 
                    type="text" 
                    required
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-cidh-gold/20 focus:border-cidh-gold outline-none transition-all"
                    value={currentSeminaire?.entreprise}
                    onChange={(e) => setCurrentSeminaire({...currentSeminaire, entreprise: e.target.value})}
                  />
                </div>

                <div>
                  <div className="space-y-2">
                    <label className="text-sm font-bold text-gray-700">Date (ex: Février 2023) *</label>
                    <input 
                      type="text" 
                      className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-cidh-gold/20 focus:border-cidh-gold outline-none transition-all"
                      value={currentSeminaire?.date_texte}
                      onChange={(e) => setCurrentSeminaire({...currentSeminaire, date_texte: e.target.value})}
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-bold text-gray-700">Financement (FCFA)</label>
                  <input 
                    type="number" 
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-cidh-gold/20 focus:border-cidh-gold outline-none transition-all"
                    value={currentSeminaire?.financement_fcfa}
                    onChange={(e) => setCurrentSeminaire({...currentSeminaire, financement_fcfa: parseFloat(e.target.value) || 0})}
                  />
                </div>

                <div className="space-y-4 md:col-span-2">
                  <label className="text-sm font-bold text-gray-700">Images du Séminaire</label>
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
                    {currentSeminaire?.images?.map((img, index) => (
                      <div key={index} className="relative group aspect-square rounded-xl overflow-hidden border border-gray-100 bg-gray-50">
                        <img 
                          src={`/media/${img}`} 
                          className="w-full h-full object-cover" 
                          alt={`Séminaire ${index + 1}`}
                        />
                        <button 
                          type="button"
                          onClick={() => removeImage(index)}
                          className="absolute top-1 right-1 p-1.5 bg-red-500 text-white rounded-full opacity-100 md:opacity-0 group-hover:opacity-100 transition-opacity shadow-lg"
                        >
                          <X size={14} />
                        </button>
                      </div>
                    ))}
                    <label className="flex flex-col items-center justify-center aspect-square border-2 border-dashed border-gray-200 rounded-xl cursor-pointer hover:bg-gray-50 hover:border-cidh-gold/50 transition-all group">
                      <div className="flex flex-col items-center justify-center p-2 text-center">
                        {isUploading ? (
                          <Loader2 className="animate-spin text-cidh-gold mb-1" size={20} />
                        ) : (
                          <Plus className="text-gray-400 group-hover:text-cidh-gold mb-1" size={20} />
                        )}
                        <p className="text-[10px] font-bold text-gray-400 group-hover:text-cidh-gold">Ajouter</p>
                      </div>
                      <input type="file" className="hidden" accept="image/*" multiple onChange={handleFileChange} />
                    </label>
                  </div>
                </div>
              </div>

              <div className="p-8 border-t flex justify-end gap-4 sticky bottom-0 bg-white">
                <button 
                  type="button"
                  onClick={() => setIsModalOpen(false)}
                  className="px-6 py-3 border border-gray-200 rounded-xl font-bold text-gray-600 hover:bg-gray-50 transition-all"
                >
                  Annuler
                </button>
                <button 
                  type="submit"
                  disabled={saveMutation.isPending || isUploading}
                  className="btn-primary flex items-center gap-2 py-3 px-8 rounded-xl shadow-lg shadow-cidh-gold/20 transition-all disabled:opacity-50"
                >
                  {saveMutation.isPending || isUploading ? <Loader2 className="animate-spin" size={20} /> : <Check size={20} />}
                  Enregistrer
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  )
}

export default AdminSeminaires
